import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import {
  PENDING_INVITE_TOKEN_KEY,
  RECENTLY_ACCEPTED_INVITE_TOKEN_KEY,
} from "../lib/workspaceInviteIntent";

interface InviteDetails {
  workspaceId: string;
  workspaceName: string;
  inviterName?: string;
  email: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  expiresAt: number;
}

export default function InvitePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState(
    () =>
      new URLSearchParams(location.hash.slice(1)).get("token") ||
      window.sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY) ||
      window.sessionStorage.getItem(RECENTLY_ACCEPTED_INVITE_TOKEN_KEY) ||
      "",
  );
  const {
    currentUser,
    loading: authLoading,
    workspaces,
    refreshWorkspaces,
    logout,
  } = useAuth();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiBase = process.env.REACT_APP_API_BASE;

  useEffect(() => {
    if (!token) {
      setInvite(null);
      setError("This invitation could not be found.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setError(null);
    window.sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
    fetch(`${apiBase}/api/invites/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ inviteToken: token }),
    })
      .then(async (response) => {
        if (!response.ok)
          throw new Error("This invitation could not be found.");
        return response.json();
      })
      .then((resolvedInvite) => {
        if (!cancelled) setInvite(resolvedInvite);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, apiBase]);

  const accept = async () => {
    if (!currentUser) {
      navigate("/login", { state: { from: location } });
      return;
    }
    setAccepting(true);
    setError(null);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${apiBase}/api/invites/accept`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ inviteToken: token }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(body.error || "This invitation cannot be accepted.");
      window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      window.sessionStorage.removeItem(RECENTLY_ACCEPTED_INVITE_TOKEN_KEY);
      await refreshWorkspaces();
      navigate("/generate", { replace: true });
    } catch (acceptError) {
      setError(
        acceptError instanceof Error
          ? acceptError.message
          : "Could not accept invitation.",
      );
    } finally {
      setAccepting(false);
    }
  };

  const alreadyJoined = invite
    ? workspaces.some((workspace) => workspace.id === invite.workspaceId)
    : false;
  const wrongAccount = error?.startsWith("Sign in as ") === true;

  useEffect(() => {
    if (invite?.status !== "accepted" || !alreadyJoined) return;
    window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
    window.sessionStorage.removeItem(RECENTLY_ACCEPTED_INVITE_TOKEN_KEY);
  }, [alreadyJoined, invite?.status]);

  return (
    <div className="min-h-screen bg-secondary px-5 py-12 sm:py-20">
      <div className="mx-auto max-w-md rounded-2xl border bg-background p-7 text-center shadow-sm sm:p-9">
        <div className="flex justify-center">
          <img src="/icon.svg" alt="WaveGen" className="h-12 w-auto" />
        </div>
        {loading ? (
          <div className="mt-6 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening
            invitation…
          </div>
        ) : invite ? (
          <>
            <h1 className="mt-6 text-2xl font-semibold tracking-tight">
              Join {invite.workspaceName} on WaveGen
            </h1>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {invite.inviterName || "A teammate"} invited you to help create,
              review, and publish social posts from one shared workspace.
            </p>
            <p className="mt-4 text-xs leading-5 text-muted-foreground">
              This invitation is for{" "}
              <strong className="font-medium text-foreground">
                {invite.email}
              </strong>
              .
            </p>
            {alreadyJoined ? (
              <Button
                className="mt-7 w-full"
                onClick={() => {
                  window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
                  window.sessionStorage.removeItem(
                    RECENTLY_ACCEPTED_INVITE_TOKEN_KEY,
                  );
                  navigate("/generate");
                }}
              >
                Open workspace
              </Button>
            ) : invite.status !== "pending" ? (
              <div className="mt-6 rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
                This invitation is {invite.status}.
              </div>
            ) : (
              <Button
                className="mt-7 w-full"
                onClick={() => void accept()}
                disabled={accepting || authLoading}
              >
                {accepting
                  ? "Joining…"
                  : currentUser
                    ? "Join workspace"
                    : "Sign in to join"}
              </Button>
            )}
          </>
        ) : null}
        {error && <p className="mt-5 text-sm text-destructive">{error}</p>}
        {wrongAccount && (
          <Button
            variant="outline"
            className="mt-3 w-full"
            onClick={async () => {
              await logout();
              navigate("/login", { state: { from: location } });
            }}
          >
            Use another account
          </Button>
        )}
      </div>
    </div>
  );
}
